import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { Info, Search } from 'lucide-react'
import { PanelError, PanelSkeleton } from '@/components/dashboards/Panel'
import { usePeople } from '@/components/analytics/explorer/analyticsQueries'
import {
  ROLE_LABEL,
  compactCurrency,
  type PeopleRole,
} from '@/services/dashboardAnalytics'

/** Roles the picker offers, people first, counterparties after. */
export const PICKER_ROLES: Array<{ role: PeopleRole; group: 'Team' | 'Counterparties' }> = [
  { role: 'underwriter', group: 'Team' },
  { role: 'responsibleParty', group: 'Team' },
  { role: 'team', group: 'Team' },
  { role: 'originator', group: 'Team' },
  { role: 'secondaryUnderwriter', group: 'Team' },
  { role: 'analyst', group: 'Team' },
  { role: 'brokerage', group: 'Counterparties' },
  { role: 'broker', group: 'Counterparties' },
  { role: 'sponsor', group: 'Counterparties' },
]

interface PeoplePickerProps {
  role: PeopleRole
  selected: string | null
  onRole: (role: PeopleRole) => void
  onSelect: (name: string) => void
  className?: string
}

/**
 * Pick a role, then a person or firm. The list is the server's: for desk roles
 * every person named on a live deal in any internal role, unioned with the
 * governed team roster (T-54) — the tooltip on the info mark says so.
 */
export default function PeoplePicker({ role, selected, onRole, onSelect, className }: PeoplePickerProps) {
  const people = usePeople(role)
  const [query, setQuery] = useState('')
  const list = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return (people.data?.people ?? []).filter((person) => !needle || person.name.toLowerCase().includes(needle))
  }, [people.data, query])

  return (
    <aside className={clsx('flex min-h-0 flex-col rounded-lg border border-amber-400/[0.09] bg-[#0a0a0a]', className)}>
      <div className="flex-shrink-0 space-y-1.5 border-b border-amber-400/[0.07] p-2">
        <label className="flex items-center gap-1.5">
          <span className="font-mono text-[8.5px] uppercase tracking-[0.18em] text-stone-500">Role</span>
          <select
            value={role}
            onChange={(event) => onRole(event.target.value as PeopleRole)}
            className="min-w-0 flex-1 rounded border border-amber-400/15 bg-[#090909] px-1.5 py-1 text-[11px] text-stone-200 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50"
          >
            {(['Team', 'Counterparties'] as const).map((group) => (
              <optgroup key={group} label={group}>
                {PICKER_ROLES.filter((entry) => entry.group === group).map((entry) => (
                  <option key={entry.role} value={entry.role}>
                    {ROLE_LABEL[entry.role]}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {people.data?.basis && (
            <span title={people.data.basis} className="text-stone-500" aria-label={people.data.basis}>
              <Info size={11} />
            </span>
          )}
        </label>
        <div className="flex items-center gap-1.5 rounded border border-amber-400/12 bg-[#090909] px-2 py-1">
          <Search size={11} className="flex-shrink-0 text-stone-600" aria-hidden="true" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Find a${role === 'underwriter' || role === 'originator' || role === 'analyst' ? 'n' : ''} ${ROLE_LABEL[role].toLowerCase()}`}
            aria-label={`Find a ${ROLE_LABEL[role].toLowerCase()}`}
            className="min-w-0 flex-1 bg-transparent text-[11px] text-stone-200 placeholder:text-stone-600 focus:outline-none"
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin p-1">
        {people.error ? (
          <div className="p-1">
            <PanelError error={people.error} onRetry={() => void people.refetch()} />
          </div>
        ) : people.isLoading ? (
          <div className="p-2">
            <PanelSkeleton rows={8} />
          </div>
        ) : list.length === 0 ? (
          <p className="p-2 text-[11px] text-stone-500">No {ROLE_LABEL[role].toLowerCase()} matches.</p>
        ) : (
          <ul className="space-y-0.5" aria-label={`${ROLE_LABEL[role]} list`}>
            {list.map((person) => {
              const active = person.name === selected
              return (
                <li key={person.name}>
                  <button
                    type="button"
                    onClick={() => onSelect(person.name)}
                    aria-current={active ? 'true' : undefined}
                    className={clsx(
                      'w-full rounded-md border px-2 py-1.5 text-left transition-colors',
                      active
                        ? 'border-amber-400/35 bg-amber-400/[0.08]'
                        : 'border-transparent hover:border-amber-400/15 hover:bg-white/[0.02]',
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={clsx('truncate text-[11.5px]', active ? 'text-amber-100' : 'text-stone-200')}>{person.name}</span>
                      <span className="flex-shrink-0 font-mono text-[10px] tabular-nums text-stone-400">{person.count}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 font-mono text-[9px] tabular-nums text-stone-600">
                      <span>{compactCurrency(person.commitment)}</span>
                      <span>{person.fundedCount} funded</span>
                      {person.quoteRatePct != null && <span>quote {person.quoteRatePct.toFixed(0)}%</span>}
                      {person.count === 0 && person.onRoster && <span className="text-stone-500">roster · no deal</span>}
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
      {people.data?.unattributed && people.data.unattributed.count > 0 && (
        <p className="flex-shrink-0 border-t border-amber-400/[0.07] px-2 py-1.5 text-[9.5px] text-stone-500">
          {people.data.unattributed.count} live deal{people.data.unattributed.count === 1 ? '' : 's'} name no{' '}
          {ROLE_LABEL[role].toLowerCase()}.
        </p>
      )}
    </aside>
  )
}
